import React from 'react';
import { useForm } from 'react-hook-form';
import { AnyEmptyQuestion, EditableQuestion } from '../../../../types';
import TitleEditor from './components/TitleEditor';
import {
  StyledButtonsContainer,
  StyledHeaderRowContainer,
  StyledQuestionEditorColumn,
  StyledQuestionEditorContainer,
  StyledTwoColumnGrid
} from './styles';
import TypeEditor from './components/TypeEditor';
import FollowUpEditor from './components/FollowUpEditor/FollowUpEditor';
import ExplanationEditor from './components/ExplanationEditor';
import RequiredEditor from './components/RequiredEditor';
import PhaseEditor from './components/PhaseEditor';
import AnswersEditor from './components/AnswersEditor/AnswersEditor';
import ImageEditor from './components/ImageEditor/ImageEditor';
import DeleteButton from './components/Buttons/DeleteButton';
import SaveButton from './components/Buttons/SaveButton';
import CancelButton from './components/Buttons/CancelButton';
import StructuredInputSwitch from './components/StructuredInputEditor/StructuredInputSwitch';
import StructuredInputBuilder from './components/StructuredInputEditor/StructuredInputBuilder';
import AnswerSelectionSwitch from './components/AnswerSelectionEditor/AnswerSelectionSwitch';
import { useShouldShowAnswersEditor } from './hooks/useShouldShowAnswersEditor';
import { useShouldShowInputEditor } from './hooks/useShouldShowInputEditor';
import { useShouldShowAnswerSelection } from './hooks/useShouldShowAnswerSelection';
import { useHiddenAnswerSelection } from './hooks/useHiddenAnswerSelection';
import { useStructuredInput } from './hooks/useStructuredInput';
import { useImageUrlResetter } from './hooks/useImageUrlResetter';

interface Props {
  question: AnyEmptyQuestion;
  index: number;
  onCancelEdit: () => void;
}

const QuestionEditor = ({ question, index, onCancelEdit }: Props) => {
  const { control, watch, handleSubmit, setValue } = useForm<EditableQuestion>({
    defaultValues: question
  });

  const shouldShowAnswersEditor = useShouldShowAnswersEditor(watch);
  const shouldShowInputEditor = useShouldShowInputEditor(watch);
  const { hasStructuredInput, setHasStructuredInput } = useStructuredInput(question, setValue, shouldShowInputEditor);
  const shouldShowAnswerSelection = useShouldShowAnswerSelection(watch);
  const { isAnswerSelectionHidden, setIsAnswerSelectionHidden } = useHiddenAnswerSelection(
    question,
    watch,
    setValue,
    shouldShowAnswerSelection
  );
  const { imageUrl } = useImageUrlResetter(watch, setValue);

  const sectionNumber = question.section || 1;
  const positionInSection = question.position + 1;

  return (
    <StyledQuestionEditorContainer>
      <StyledHeaderRowContainer>
        Section {sectionNumber}, Question {positionInSection}
        <CancelButton onClick={onCancelEdit} />
      </StyledHeaderRowContainer>
      <StyledQuestionEditorColumn>
        <StyledTwoColumnGrid>
          <StyledQuestionEditorColumn>
            <TitleEditor control={control} />
            <TypeEditor control={control} />
            <RequiredEditor control={control} isNeverRequired={isAnswerSelectionHidden} />
            <PhaseEditor control={control} />
            {shouldShowAnswersEditor && (
              <AnswersEditor
                control={control}
                canAddAnswers={!isAnswerSelectionHidden}
                canAddBlockingAnswers={!isAnswerSelectionHidden}
              />
            )}
            {shouldShowInputEditor && (
              <StructuredInputSwitch checked={hasStructuredInput} onChange={setHasStructuredInput} />
            )}
            {shouldShowAnswerSelection && (
              <AnswerSelectionSwitch
                control={control}
                checked={isAnswerSelectionHidden}
                onChange={setIsAnswerSelectionHidden}
              />
            )}
          </StyledQuestionEditorColumn>
          <StyledQuestionEditorColumn>
            <ImageEditor imageUrl={imageUrl} control={control} />
            <FollowUpEditor setValue={setValue} question={question} watch={watch} control={control} />
          </StyledQuestionEditorColumn>
        </StyledTwoColumnGrid>
        {/* hasStructuredInput already folds in the question type: retyping away from free
            text turns it off and nulls `input`, so nothing is left in the save payload for
            the builder that just unmounted. See useStructuredInput. */}
        {hasStructuredInput && <StructuredInputBuilder control={control} setValue={setValue} />}
        <ExplanationEditor control={control} />
        <StyledButtonsContainer>
          <DeleteButton question={question} />
          <SaveButton handleSubmit={handleSubmit} question={question} />
        </StyledButtonsContainer>
      </StyledQuestionEditorColumn>
    </StyledQuestionEditorContainer>
  );
};

export default QuestionEditor;
